import { Injectable } from '@angular/core';
import {API} from '@app/models';
import {TreeNode} from 'primeng/api';
import {ApiService} from './api.service';
import {Observable, forkJoin, from, map, of, shareReplay, finalize} from 'rxjs';
import { sortTreeNodes } from '@app/helpers/helper-functions';
import { sortNestedTree } from '@app/helpers/sort-nested-tree';

@Injectable({
  providedIn: 'root'
})
export class UserSelectService {
  filteredusers: API.User[] = [];
  private usersCache: Map<string, API.User[]> = new Map();
  private usersInFlight: Map<string, Observable<any[]>> = new Map();
  availableWorkplaces: any[] = [];
  availableWorkplacesNoRestrictions: any[] = [];
  availableWorkplacesFiltered: Map<number, any[]> = new Map();
  private workplacesInFlight$: Observable<any[]> | null = null;
  private workplacesNoRestrictionsInFlight$: Observable<any[]> | null = null;
  private workplacesFilteredInFlight: Map<number, Observable<any[]>> = new Map();
  availableGroupsTree: any[] = [];
  availableGroupsFlat: any[] = [];
  availableGroupsFlatNoRestrictions: any[] = [];
  availableGroupsFlatFiltered: Map<number, any[]> = new Map();
  availableUtilities: any[] = [];
  availableWorkplacesIncludeUtilities: TreeNode[] = [];
  availableWorkplacesIncludeUtilitiesNoRestrictions: TreeNode[] = [];
  selectedWorkPlace: any;
  workplaces: TreeNode[] = [];
  workplacesLoading: boolean = false;
  users: API.User[] = [];
  selectedUser: any;
  usersLoading: boolean = false;

  constructor(
    private api: ApiService
  ) { }

  /**
   * Deep structural copy of a node tree. The service caches are singletons whose
   * arrays/nodes are shared by every consumer; some consumers mutate what they
   * receive (e.g. list-mode workplace-select writes a circular `parent` back-ref),
   * which corrupts the cache and later crashes renameTreeKeys with a stack overflow.
   * Returning a clone on every emit keeps the cache pristine and immutable to callers.
   */
  private cloneNodes(nodes: any[]): any[] {
    return (nodes || []).map((node: any) => {
      const copy: any = { ...node };
      delete copy.parent;
      if (node && node.children) {
        copy.children = this.cloneNodes(node.children);
      }
      return copy;
    });
  }

  getWorkPlaces(clientId?: number, noRestrictions: boolean = false): Observable<any[]> {
    // If no clientId, use the unfiltered cache. The restricted and
    // no-restrictions variants are cached separately so both can coexist.
    if (!clientId) {
      const cache = noRestrictions ? this.availableWorkplacesNoRestrictions : this.availableWorkplaces;
      if (cache.length !== 0) {
        return of(this.cloneNodes(cache));
      }
      const inFlight = noRestrictions ? this.workplacesNoRestrictionsInFlight$ : this.workplacesInFlight$;
      if (inFlight) {
        return inFlight.pipe(map((r: any[]) => this.cloneNodes(r)));
      }
    } else {
      // If clientId is provided, check if it exists in the Map
      if (this.availableWorkplacesFiltered.has(clientId)) {
        return of(this.cloneNodes(this.availableWorkplacesFiltered.get(clientId)!));
      }
      // De-dupe concurrent identical requests for the same client so the same list
      // isn't fetched twice before the first response resolves (e.g. two workplace
      // selectors reacting to the same client change in one change-detection tick).
      const inFlightForClient = this.workplacesFilteredInFlight.get(clientId);
      if (inFlightForClient) {
        return inFlightForClient.pipe(map((r: any[]) => this.cloneNodes(r)));
      }
    }

    // Build API params
    const params: any = { flat_list: false, simple: true };
    if (clientId) {
      params.client_id = clientId;
    }
    if (noRestrictions) {
      params.no_restrictions = true;
    }

    const request$ = from(this.api.getFromURL('/api/v1/groups', params)).pipe(
      map((wPlaces: any) => {
        const enrichNode = (node: any): any => {
          const enrichedNode = {
            ...node,
            data: { tag: node.tag },
            disabled: false
          };

          if (node.children && node.children.length > 0) {
            enrichedNode.children = node.children.map((child: any) => enrichNode(child));
          }

          return enrichedNode;
        };

        const workplaces = (wPlaces.groups || []).map((place: any) => enrichNode(place));

        // Store in appropriate cache
        if (!clientId) {
          if (noRestrictions) {
            this.availableWorkplacesNoRestrictions = workplaces;
          } else {
            this.availableWorkplaces = workplaces;
          }
        } else {
          // Cache per client so re-selecting a previously fetched client is a cache hit
          // (the map is fully invalidated on logout / user-group mutations).
          this.availableWorkplacesFiltered.set(clientId, workplaces);
        }

        return workplaces;
      }),
      finalize(() => {
        if (!clientId) {
          if (noRestrictions) {
            this.workplacesNoRestrictionsInFlight$ = null;
          } else {
            this.workplacesInFlight$ = null;
          }
        } else {
          this.workplacesFilteredInFlight.delete(clientId);
        }
      }),
      shareReplay(1)
    );

    if (!clientId) {
      if (noRestrictions) {
        this.workplacesNoRestrictionsInFlight$ = request$;
      } else {
        this.workplacesInFlight$ = request$;
      }
    } else {
      this.workplacesFilteredInFlight.set(clientId, request$);
    }

    // Cache holds the pristine array (set inside the map); hand each subscriber its own clone.
    return request$.pipe(map((r: any[]) => this.cloneNodes(r)));
  }

  getUtilities(): Observable<any[]> {
    if(this.availableUtilities.length!==0){
      return of(this.availableUtilities);
    }
    return from(this.api.getFromURL('/api/v1/utilities',{flat_list:true, simple: true})).pipe(
      map((resp: any) => {
        this.availableUtilities = resp.utilities
        return this.availableUtilities;
      })
    );
  }

  getWorkplacesIncludeUtilities(noRestrictions: boolean = false): Observable<TreeNode[]> {
    // Cache the restricted and no-restrictions variants separately, and forward
    // noRestrictions to getWorkPlaces so the flag isn't dropped on this path.
    const cache = noRestrictions ? this.availableWorkplacesIncludeUtilitiesNoRestrictions : this.availableWorkplacesIncludeUtilities;
    if (cache.length !== 0) {
      return of(this.cloneNodes(cache));
    }

    return forkJoin([this.getWorkPlaces(undefined, noRestrictions), this.getUtilities()]).pipe(
      map(([workplaces, utilities]) => {
        const addUtilitiesToNodes = (nodes: any[]): TreeNode[] => {
          return nodes.map(node => {
            const children = node.children ? addUtilitiesToNodes(node.children) : [];
            const matchingUtilities = utilities
              .filter(util => util.group_id === node.id)
              .map(util => ({
                key: `u-${util.id}`,
                label: util.name,
                data: {tag: 'utility'}
              }));

            return {
              label: node.name,
              key: node.id,
              data: {tag: node.tag},
              children: [...children, ...matchingUtilities]
            };
          });
        };

        const result = addUtilitiesToNodes(workplaces);
        sortTreeNodes(result);
        if (noRestrictions) {
          this.availableWorkplacesIncludeUtilitiesNoRestrictions = result;
        } else {
          this.availableWorkplacesIncludeUtilities = result;
        }
        return this.cloneNodes(result);
      })
    );
  }

  getUsers(short:boolean=true, scope:string='admin', roles: string[]=['client_admin','manager','checker'], usersUrl: string = '/api/v1/users', noRestrictions: boolean = false): Observable<any[]> {
    let params = `short=${short}`;
    if (scope) {
      params += `&scope=${scope}`;
    }
    for(let role of roles){
      params+='&roles[]='+role;
    }
    if (noRestrictions) {
      params += '&no_restrictions=true';
    }

    // Cache each distinct query (usersUrl + scope + roles + no_restrictions) on its
    // own key, so the restricted and no-restrictions lists can coexist.
    const cacheKey = `${usersUrl}?${params}`;
    if (this.usersCache.has(cacheKey)) {
      return of(this.usersCache.get(cacheKey)!);
    }
    // De-dupe concurrent identical requests so the same list isn't fetched twice.
    if (this.usersInFlight.has(cacheKey)) {
      return this.usersInFlight.get(cacheKey)!;
    }

    this.usersLoading = true;
    const request$ = from(this.api.getFromURL(cacheKey)).pipe(
      map((users: any[]) => {
        const mapped = users.map((user: any) => {
          return {
            ...user,
            id: user.id,
            name: user.full_name,
          };
        });
        if (mapped.length) {
          this.usersCache.set(cacheKey, mapped);
        }
        this.filteredusers = this.users = mapped;
        this.usersLoading = false;
        return mapped;
      }),
      finalize(() => this.usersInFlight.delete(cacheKey)),
      shareReplay(1)
    );
    this.usersInFlight.set(cacheKey, request$);
    return request$;
  }

  /**
   * Returns the full group hierarchy as a sorted tree.
   * `/api/v1/groups?flat_list=false&simple=true`. Migrated from UsersGroupsService.
   */
  getGroupsTree(): Observable<any[]> {
    if (this.availableGroupsTree.length !== 0) {
      return of(this.cloneNodes(this.availableGroupsTree));
    }
    return from(this.api.getFromURL('/api/v1/groups', {flat_list: false, simple: true})).pipe(
      map((wPlaces: any) => {
        const groups = (wPlaces.groups || []).map((place: any) => ({
          ...place,
          disabled: false
        }));
        this.availableGroupsTree = sortNestedTree(groups, 'name');
        return this.cloneNodes(this.availableGroupsTree);
      })
    );
  }

  /**
   * Returns groups as a flat list.
   * `/api/v1/groups?flat_list=true`. Migrated from UsersGroupsService.
   */
  getGroups(noRestrictions: boolean = false, clientId?: number): Observable<any[]> {
    // When a clientId is given, return that client's flat groups (cached per client);
    // otherwise the current user's flat groups (cached by restricted/unrestricted).
    if (clientId) {
      if (this.availableGroupsFlatFiltered.has(clientId)) {
        return of(this.cloneNodes(this.availableGroupsFlatFiltered.get(clientId)!));
      }
    } else {
      const cache = noRestrictions ? this.availableGroupsFlatNoRestrictions : this.availableGroupsFlat;
      if (cache.length !== 0) {
        return of(this.cloneNodes(cache));
      }
    }
    const params: any = { flat_list: true };
    if (clientId) {
      params.client_id = clientId;
    }
    if (noRestrictions) {
      params.no_restrictions = true;
    }
    return from(this.api.getFromURL('/api/v1/groups', params)).pipe(
      map((wPlaces: any) => {
        const groups = (wPlaces.groups || []).map((place: any) => ({
          ...place,
          disabled: false
        }));
        if (clientId) {
          this.availableGroupsFlatFiltered.set(clientId, groups);
        } else if (noRestrictions) {
          this.availableGroupsFlatNoRestrictions = groups;
        } else {
          this.availableGroupsFlat = groups;
        }
        return this.cloneNodes(groups);
      })
    );
  }

  /**
   * Clears all cached data in the service.
   * Should be called during logout to prevent data leakage between sessions.
   */
  clearCache(): void {
    this.filteredusers = [];
    this.availableWorkplaces = [];
    this.availableWorkplacesNoRestrictions = [];
    this.availableWorkplacesFiltered.clear();
    this.availableGroupsTree = [];
    this.availableGroupsFlat = [];
    this.availableGroupsFlatNoRestrictions = [];
    this.availableGroupsFlatFiltered.clear();
    this.availableUtilities = [];
    this.availableWorkplacesIncludeUtilities = [];
    this.availableWorkplacesIncludeUtilitiesNoRestrictions = [];
    this.selectedWorkPlace = null;
    this.workplaces = [];
    this.workplacesLoading = false;
    this.users = [];
    this.selectedUser = null;
    this.usersLoading = false;
    this.usersCache.clear();
    this.usersInFlight.clear();
    this.workplacesInFlight$ = null;
    this.workplacesNoRestrictionsInFlight$ = null;
    this.workplacesFilteredInFlight.clear();
  }

  /**
   * Invalidate the cached users + group/workplace lists so the next request refetches.
   * Call after a user or group is created / edited / (de)activated / removed.
   */
  invalidateUsersAndGroups(): void {
    // users
    this.usersCache.clear();
    this.usersInFlight.clear();
    // groups / workplaces (all cached variants + utilities) and their in-flight refs
    this.availableWorkplaces = [];
    this.availableWorkplacesNoRestrictions = [];
    this.availableWorkplacesFiltered.clear();
    this.availableGroupsTree = [];
    this.availableGroupsFlat = [];
    this.availableGroupsFlatNoRestrictions = [];
    this.availableGroupsFlatFiltered.clear();
    this.availableUtilities = [];
    this.availableWorkplacesIncludeUtilities = [];
    this.availableWorkplacesIncludeUtilitiesNoRestrictions = [];
    this.workplacesInFlight$ = null;
    this.workplacesNoRestrictionsInFlight$ = null;
    this.workplacesFilteredInFlight.clear();
  }
  
}
